import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, ArrowRight, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { cliente: "Cliente", partner: "Partner", prospect: "Prospect" };

export default function AutoDedupDialog({ open, onOpenChange, onDone }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState(null);
  const [autoOn, setAutoOn] = useState(false);

  const loadDryRun = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.post("/anagrafiche/auto-dedup", { apply: false }),
        api.get("/anagrafiche/auto-dedup/status").catch(() => ({ data: { enabled: false } })),
      ]);
      setData(r.data);
      setAutoOn(!!s.data?.enabled);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel calcolo dei doppioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setData(null); loadDryRun(); }
  }, [open]);

  const apply = async () => {
    if (!window.confirm(`Confermi l'unione automatica di ${data?.to_merge || 0} doppioni in ${data?.groups || 0} anagrafiche?\n\nAttività, referenti, preventivi e note vengono spostati sul record con P.IVA. Viene creato un backup automatico.`)) return;
    setApplying(true);
    try {
      const r = await api.post("/anagrafiche/auto-dedup", { apply: true });
      toast.success(`Unione completata: ${r.data.merged} doppioni uniti in ${r.data.groups} anagrafiche`);
      onDone?.();
      await loadDryRun();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unione non riuscita");
    } finally {
      setApplying(false);
    }
  };

  const toggleAuto = async (v) => {
    setAutoOn(v);
    try {
      await api.post("/anagrafiche/auto-dedup/toggle", { enabled: v });
      toast.success(v ? "Automatismo attivato: girerà dopo ogni sync Fier" : "Automatismo disattivato");
    } catch (e) {
      setAutoOn(!v);
      toast.error(e.response?.data?.detail || "Impossibile aggiornare l'automatismo");
    }
  };

  const det = data?.detail || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="auto-dedup-dialog">
        <DialogHeader>
          <DialogTitle>Deduplica automatica per nome</DialogTitle>
          <DialogDescription>
            Unisce i doppioni con lo <b>stesso nome</b> nel record con <b>P.IVA</b> (l'azienda "vera").
            I gruppi con P.IVA diverse restano intatti. Anteprima senza modifiche: rivedi e poi applica.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span data-testid="auto-dedup-auto-label">Esegui in automatico dopo ogni sync Fier</span>
          </div>
          <Switch checked={autoOn} onCheckedChange={toggleAuto} data-testid="auto-dedup-auto-toggle" />
        </div>

        {loading && <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>}

        {!loading && data && (
          <>
            <div className="flex items-center justify-between">
              <div className="text-sm" data-testid="auto-dedup-summary">
                <b>{data.groups}</b> anagrafiche con doppioni · <b>{data.to_merge}</b> record da unire
              </div>
              <Button variant="outline" size="sm" onClick={loadDryRun} data-testid="auto-dedup-refresh"><RefreshCw className="mr-2 h-3.5 w-3.5" /> Ricalcola</Button>
            </div>

            {det.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground" data-testid="auto-dedup-empty">
                Nessun doppione sicuro da unire. 🎉
              </div>
            )}

            {det.length > 0 && (
              <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1" data-testid="auto-dedup-list">
                {det.map((e, i) => (
                  <div key={i} className="rounded-md border border-border p-2.5 text-xs" data-testid={`auto-dedup-group-${i}`}>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="border-emerald-300 bg-emerald-100 text-[10px] text-emerald-800">{KIND_LABEL[e.survivor.kind] || e.survivor.kind}</Badge>
                      <span className="font-semibold">{e.survivor.name}</span>
                      <span className="text-muted-foreground">· P.IVA {e.survivor.piva || "—"} · {[e.survivor.city, e.survivor.province].filter(Boolean).join(" ") || "—"} · {e.survivor.activities} att.</span>
                    </div>
                    {e.losers.map((l, j) => (
                      <div key={j} className="mt-1 flex items-center gap-1.5 pl-2 text-[11px] text-muted-foreground">
                        <ArrowRight className="h-3 w-3 shrink-0 text-red-400" />
                        <Badge variant="outline" className="text-[10px]">{KIND_LABEL[l.kind] || l.kind}</Badge>
                        <span className="text-red-500 line-through">{l.name}</span>
                        <span>· {l.activities} att. · {l.reason}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="auto-dedup-close">Chiudi</Button>
              <Button onClick={apply} disabled={applying || data.to_merge === 0} data-testid="auto-dedup-apply">
                {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Applica unione ({data.to_merge})
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
